import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { openDialog } from '@/features/dialog/dialogSlice';
import { useGetUserDetailsQuery, useGetUsersByBrokerGroupIdQuery } from '@/features/user/userApi';
import { NoRowsOverlay } from '@/components/table/DataGridComponents/NoRowsOverlay';
import { columns as brokerageUserColumns } from '@/components/views/SettingsView/BrokerageUsersTable/data';
import { mapBrokerageUserRow } from '@/components/views/SettingsView/BrokerageUsersTable/helpers';

export const BrokerageUsers = ({

	brokerGroupId,
}) => {
	const dispatch = useDispatch();
	const account = useSelector((state) => state.msalAccount.account);

	const { data: currentUser } = useGetUserDetailsQuery(
		{ email: account?.username },
		{ skip: !account?.username },
	);

	const canViewGroup = Boolean(currentUser?.canViewGroup);

	const { data: users = [], isLoading: isLoadingUsers } = useGetUsersByBrokerGroupIdQuery(
		brokerGroupId,
		{ skip: !brokerGroupId || !canViewGroup },
	);

	const userRows = useMemo(
		() => users.map((user) => mapBrokerageUserRow({ ...user.data, id: user.id })),
		[users],
	);

	const userColumns = useMemo(
		() =>
			brokerageUserColumns.filter(
				(column) => column.field !== 'brokerGroupIds',
			),
		[],
	);

	const handleRowClick = ({ row }) => {
		// row.id is the user's Firestore doc id; UserProfileDrawer reads data.userId.
		if (!row?.id) {
			return;
		}

		dispatch(
			openDialog({
				dialogName: 'userProfile',
				data: {
					userId: row.id,
				},
			}),
		);
	};

	return (
		<Stack spacing={1}>
			<Typography variant='h6'>Users</Typography>

			<Box
				sx={{
					border: 1,
					borderColor: 'divider',
					borderRadius: '8px',
					overflow: 'hidden',
				}}
			>
				<DataGrid
					autoHeight
					loading={isLoadingUsers}
					rows={userRows}
					columns={userColumns}
					disableRowSelectionOnClick
					hideFooterPagination
					hideFooter
					disableColumnFilter
					onRowClick={handleRowClick}
					sx={{
						border: 0,
						'& .MuiDataGrid-cell': {
							outline: 'none',
						},
						'& .MuiDataGrid-row': {
							cursor: 'pointer',
						},
						'--DataGrid-overlayHeight': '3em',
					}}
					slots={{
						noRowsOverlay: () => (
							<NoRowsOverlay objectType='users' />
						),
						noResultsOverlay: () => (
							<NoRowsOverlay objectType='users' />
						),
					}}
					slotProps={{
						loadingOverlay: {
							variant: 'skeleton',
							noRowsVariant: 'skeleton',
						},
					}}
				/>
			</Box>
		</Stack>
	);
};

export default BrokerageUsers;